import { memo } from 'react';
import { Box, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { NumericFormat } from 'react-number-format';
import { TextColor } from '@setvi/shared/enums';
import { ProfitMarginPercentageInput } from 'pages/quotes/components/quote-items/components/profit-margin-percentage-input';
import type { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { FirstRowBox, RowContainer, SecondRowBox } from '../styled';

interface GpColumnProps {
  product: QuoteProductDTO;
  productId: string;
  onProfitMarginChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  onProfitMarginPercentageChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const useStyles = makeStyles(() => ({
  input: {
    width: '110px',
    textAlign: 'center',
    borderRadius: '99px !important',
    border: '1px solid #e2e2ea',
    height: '40px',
    padding: '10px',
    fontSize: '16px',
    backgroundColor: '#F8F9FA',
    '& input[type=number]': {
      MozAppearance: 'textfield',
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        display: 'none'
      }
    }
  }
}));

export const GpColumn = memo(
  ({
    product,
    productId,
    onProfitMarginChange,
    onProfitMarginPercentageChange,
    disabled = false,
    disabledTooltip = ''
  }: GpColumnProps) => {
    const classes = useStyles();

    if (product?.isPricingFetching) {
      return (
        <RowContainer>
          <FirstRowBox>
            <Box display="flex" gridGap="15px" width="100%">
              <Skeleton variant="rect" width="50%" height={40} />
              <Skeleton variant="rect" width="50%" height={40} />
            </Box>
          </FirstRowBox>
          <SecondRowBox />
        </RowContainer>
      );
    }

    const isInputDisabled =
      disabled || (!product?.isEditable && !product?.isFreeHand);

    const gpInputs = (
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
        <NumericFormat
          value={product?.totalGrossProfit}
          decimalScale={2}
          fixedDecimalScale
          thousandSeparator=","
          decimalSeparator="."
          prefix="$ "
          onChange={e => {
            onProfitMarginChange(
              productId,
              product,
              e.target.value.replace(/[^\d.-]/g, '')
            );
          }}
          disabled={isInputDisabled}
          className={classes.input}
          style={{
            width: 145,
            fontWeight: 600,
            color:
              product?.totalGrossProfitPercentage > 0
                ? TextColor.Green
                : TextColor.Red
          }}
        />
        <ProfitMarginPercentageInput
          value={product?.totalGrossProfitPercentage}
          productId={productId}
          product={product}
          isEditable={!disabled && (product?.isEditable || product?.isFreeHand)}
          maxValue={
            product?.maxGpPercentage ? Number(product?.maxGpPercentage) : 99.99
          }
          minValue={
            product?.minGpPercentage ? Number(product?.minGpPercentage) : 0
          }
          onProfitMarginPercentageChange={onProfitMarginPercentageChange}
          inputClassName={classes.input}
          textWithColors
        />
      </Box>
    );

    return (
      <RowContainer>
        <FirstRowBox>
          {disabled && disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow>
              <Box>{gpInputs}</Box>
            </Tooltip>
          ) : (
            gpInputs
          )}
        </FirstRowBox>
        <SecondRowBox />
      </RowContainer>
    );
  }
);

GpColumn.displayName = 'GpColumn';
